import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyThemePreference,
  getStoredThemePreference,
  getSystemTheme,
  setStoredThemePreference,
  subscribeToSystemTheme,
} from '../utils/theme';
import { useAuth } from '../context/AuthContext';

const VALID_PREFERENCES = ['light', 'dark', 'system'];

const normalizePreference = (value) => {
  if (VALID_PREFERENCES.includes(value)) {
    return value;
  }
  return 'system';
};

const resolveTheme = (preference) => {
  if (preference === 'dark') {
    return 'dark';
  }
  if (preference === 'light') {
    return 'light';
  }
  return getSystemTheme();
};

const useThemePreference = () => {
  const initialPreference = useMemo(() => normalizePreference(getStoredThemePreference()), []);
  const [preference, setPreference] = useState(initialPreference);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(initialPreference));
  const { user } = useAuth();

  useEffect(() => {
    const normalized = normalizePreference(preference);
    setStoredThemePreference(normalized);
    const applied = applyThemePreference(normalized);
    setResolvedTheme(applied);
  }, [preference]);

  useEffect(() => {
    const remotePreference = normalizePreference(user?.settings?.themePreference);
    if (
      user
      && typeof user.settings?.themePreference === 'string'
      && remotePreference !== preference
    ) {
      setPreference(remotePreference);
    }
  }, [preference, user]);

  useEffect(() => {
    if (preference !== 'system') {
      return () => {};
    }
    const unsubscribe = subscribeToSystemTheme((nextTheme) => {
      setResolvedTheme(nextTheme);
      applyThemePreference('system');
    });
    return () => {
      unsubscribe();
    };
  }, [preference]);

  const updatePreference = useCallback((nextPreference) => {
    setPreference(normalizePreference(nextPreference));
  }, []);

  return {
    preference,
    resolvedTheme,
    setPreference: updatePreference,
  };
};

export default useThemePreference;
